import { Eye } from "lucide-react";
import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { textCell } from "@/components/ui/table-cell";
import { formatDate } from "@/lib/format";
import { useWatches } from "@/hooks/useWatches";
import { WATCH_KIND_LABELS, type Watch } from "@/types/watches";
import { WatchRowMenu } from "./WatchRowMenu";

export function WatchesPage() {
  const { data, isLoading, isError } = useWatches();

  const columns = useMemo<DataTableColumn<Watch>[]>(
    () => [
      {
        accessorKey: "watch_kind",
        header: "Watching for",
        size: 200,
        cell: ({ row }) => textCell(WATCH_KIND_LABELS[row.original.watch_kind]),
      },
      {
        accessorKey: "entity_label",
        header: "Target",
        size: 240,
        cell: ({ row }) => textCell(row.original.entity_label),
      },
      {
        accessorKey: "threshold_days",
        header: "Threshold",
        size: 120,
        cell: ({ row }) =>
          textCell(
            row.original.threshold_days != null
              ? `${row.original.threshold_days} days`
              : null,
          ),
      },
      {
        accessorKey: "last_fired_at",
        header: "Last notified",
        size: 140,
        cell: ({ row }) =>
          textCell(
            row.original.last_fired_at ? formatDate(row.original.last_fired_at) : null,
          ),
      },
      {
        accessorKey: "created_at",
        header: "Created",
        size: 140,
        cell: ({ row }) => textCell(formatDate(row.original.created_at)),
      },
      {
        accessorKey: "active",
        header: "Status",
        size: 110,
        cell: ({ row }) =>
          row.original.active ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge>Cancelled</Badge>
          ),
      },
      {
        id: "actions",
        header: "",
        size: 48,
        enableSorting: false,
        cell: ({ row }) => <WatchRowMenu watch={row.original} />,
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-5">
      {isError && (
        <div className="rounded-md border border-destructive-dim bg-destructive-dim p-3 text-sm text-destructive">
          Failed to load watches.
        </div>
      )}

      <DataTable<Watch>
        columns={columns}
        data={data ?? []}
        isLoading={isLoading}
        defaultSort={[{ id: "created_at", desc: true }]}
        emptyMessage={
          <EmptyState
            icon={Eye}
            title="You're not watching anything yet"
            description="Ask Maxwell to keep an eye on a deal, contract, or supplier — your watches show up here."
          />
        }
      />
    </div>
  );
}

export default WatchesPage;
